import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { getAddOns, createAddOn, updateAddOn, deleteAddOn } from "store/add-ons"

export const useAddOns = ({ enabled: fetchEnabled = false } = {}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const addOns = useSelector((state) => state.addOns?.items || [])
  const addOnsLoading = useSelector((state) => state.addOns?.loading || false)
  const isCreating = useSelector((state) => state.addOns?.createAddOn?.loading || false)
  const isUpdating = useSelector((state) => state.addOns?.updateAddOn?.loading || false)
  const isDeleting = useSelector((state) => state.addOns?.deleteAddOn?.loading || false)

  const isMutating = isCreating || isUpdating || isDeleting

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [addOnToEdit, setAddOnToEdit] = useState(null)

  // Single confirm-modal state shared by delete/activate/deactivate.
  const [confirmAction, setConfirmAction] = useState(null)

  // Manage-experiences modal (assigns an add-on to a set of trips, mirrors equipment).
  const [assignExpAddOn, setAssignExpAddOn] = useState(null)

  useEffect(() => {
    if (fetchEnabled) dispatch(getAddOns())
  }, [dispatch, fetchEnabled])

  // Resolves with the newly created add-on (null on edit or failure) so callers can select it
  const handleAddAddOn = async (formData) => {
    let createdAddOn = null
    try {
      if (addOnToEdit) {
        await dispatch(updateAddOn(addOnToEdit.id, formData))
      } else {
        const result = await dispatch(createAddOn(formData))
        createdAddOn = result?.data || result || null
      }
      dispatch(getAddOns())
      setIsModalOpen(false)
      setAddOnToEdit(null)
    } catch {
      // Errors surface via the toaster from the api() helper.
    }
    return createdAddOn
  }

  const handleConfirm = async () => {
    if (!confirmAction) return
    const { type, addOn } = confirmAction
    try {
      if (type === "delete") {
        await dispatch(deleteAddOn(addOn.id))
      } else {
        const formData = new FormData()
        formData.append("add_on[enabled]", type === "activate")
        await dispatch(updateAddOn(addOn.id, formData))
      }
      dispatch(getAddOns())
      setConfirmAction(null)
    } catch {
      // Errors surface via the toaster from the api() helper.
    }
  }

  const handleAssignExperiences = async (selectedExperienceIds) => {
    if (!assignExpAddOn) return
    const formData = new FormData()
    if (selectedExperienceIds.length === 0) {
      formData.append("add_on[trip_ids][]", "")
    } else {
      for (const id of selectedExperienceIds) formData.append("add_on[trip_ids][]", id)
    }
    await dispatch(updateAddOn(assignExpAddOn.id, formData))
    dispatch(getAddOns())
    setAssignExpAddOn(null)
  }

  const getMenuItems = (record) => [
    {
      key: "edit",
      label: t("settings.action_edit"),
      onClick: () => {
        setAddOnToEdit(record)
        setIsModalOpen(true)
      }
    },
    {
      key: "manage-experiences",
      label: t("settings.add_ons.action_manage_experiences", { defaultValue: "Manage Experiences" }),
      onClick: () => setAssignExpAddOn(record)
    },
    {
      key: "toggle",
      label: record.enabled ? t("settings.action_deactivate") : t("settings.action_activate"),
      onClick: () => setConfirmAction({ type: record.enabled ? "deactivate" : "activate", addOn: record })
    },
    {
      key: "delete",
      label: t("settings.action_delete"),
      danger: true,
      onClick: () => setConfirmAction({ type: "delete", addOn: record })
    }
  ]

  return {
    addOns,
    addOnsLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isMutating,
    isModalOpen,
    addOnToEdit,
    handleAddAddOn,
    openAddModal: () => {
      setAddOnToEdit(null)
      setIsModalOpen(true)
    },
    closeModal: () => {
      setIsModalOpen(false)
      setAddOnToEdit(null)
    },
    getMenuItems,
    confirmAction,
    closeConfirm: () => setConfirmAction(null),
    handleConfirm,
    assignExpAddOn,
    openAssignExperiences: (record) => setAssignExpAddOn(record),
    closeAssignExperiences: () => setAssignExpAddOn(null),
    handleAssignExperiences
  }
}
